import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowDropDown,
  ArrowDropUp,
  DeleteOutline,
  UndoOutlined,
  UnfoldMore,
} from "@mui/icons-material";
import { tokens, withAlpha } from "../../Common/designTokens";

export const WATCHLIST_TABLE_ROW_HEIGHT = 32;
export const WATCHLIST_DEFAULT_ROWS_PER_PAGE = 50;
export const WATCHLIST_TABLE_VIEWPORT_HEIGHT =
  WATCHLIST_TABLE_ROW_HEIGHT * (WATCHLIST_DEFAULT_ROWS_PER_PAGE + 1);

export const getMaxRowsPerPageForViewport = (viewportHeight) => {
  const safeViewportHeight = Number(viewportHeight) || WATCHLIST_TABLE_VIEWPORT_HEIGHT;
  return Math.max(
    WATCHLIST_DEFAULT_ROWS_PER_PAGE,
    Math.floor(safeViewportHeight / WATCHLIST_TABLE_ROW_HEIGHT) - 1
  );
};

const WATCHLIST_SORTABLE_COLUMNS = new Set(["cusip", "securityDesc", "bondType"]);

const EmptyState = ({ message, loading = false, viewportHeight }) => (
  <Box
    sx={{
      height: viewportHeight,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      alignItems: "center",
      justifyContent: "center",
      px: 3,
      textAlign: "center",
      color: tokens.colors.fontColour85,
      backgroundColor: tokens.colors.bodyBackgroundColor,
    }}
  >
    {loading ? <CircularProgress size={24} /> : null}
    <Typography>{message}</Typography>
  </Box>
);

const WatchlistCheckboxIcon = ({
  strokeColor,
  fillColor = "transparent",
  checkColor,
}) => (
  <SvgIcon viewBox="0 0 24 24">
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth="2"
      rx="2"
      ry="2"
    />
    {checkColor ? (
      <path
        d="M7 12.5L10.5 16l6.5-6.5"
        stroke={checkColor}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    ) : null}
  </SvgIcon>
);

const SortableHeaderLabel = ({ label, columnKey, sortConfig, onSortChange }) => {
  const isSortable = WATCHLIST_SORTABLE_COLUMNS.has(columnKey);
  const isActiveSort = sortConfig?.key === columnKey;
  const handleSortClick = () => {
    if (!isSortable) {
      return;
    }

    onSortChange?.(columnKey);
  };

  return (
    <Box
      onClick={handleSortClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: columnKey === "action" ? "center" : "flex-start",
        gap: 0.25,
        width: "100%",
        cursor: isSortable ? "pointer" : "default",
      }}
    >
      <Typography
        component="span"
        sx={{ fontWeight: 700, color: tokens.colors.fontColour }}
      >
        {label}
      </Typography>
      {isSortable ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: tokens.colors.fontColour,
          }}
        >
          {isActiveSort ? (
            sortConfig?.direction === "asc" ? (
              <ArrowDropUp fontSize="small" />
            ) : (
              <ArrowDropDown fontSize="small" />
            )
          ) : (
            <UnfoldMore
              fontSize="small"
              sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
            />
          )}
        </Box>
      ) : null}
    </Box>
  );
};

const MAWatchlistTableSection = ({
  bonds,
  actionLabel,
  onActionClick,
  isActionDisabled,
  useIconAction = false,
  useCheckboxAction = false,
  isCheckboxChecked,
  isCheckboxDisabled,
  onCheckboxToggle,
  isDiscarded,
  getActionLabel,
  emptyMessage,
  loading = false,
  loadingMessage = "Loading watchlist...",
  sortConfig,
  onSortChange,
  descriptionSortKey = "securityDesc",
  viewportHeight = WATCHLIST_TABLE_VIEWPORT_HEIGHT,
}) => {
  if (loading) {
    return <EmptyState message={loadingMessage} loading viewportHeight={viewportHeight} />;
  }

  if (bonds.length === 0) {
    return <EmptyState message={emptyMessage} viewportHeight={viewportHeight} />;
  }

  return (
    <TableContainer
      sx={{
        height: viewportHeight,
        maxHeight: viewportHeight,
        overflowY: "auto",
        overflowX: "hidden",
        backgroundColor: tokens.colors.bodyBackgroundColor,
      }}
    >
      <Table stickyHeader sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: 120,
                height: WATCHLIST_TABLE_ROW_HEIGHT,
                py: 0.75,
                textAlign: "center",
                color: tokens.colors.fontColour,
                backgroundColor: tokens.colors.defaultGridHeaderBackground,
                borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
              }}
            >
              <SortableHeaderLabel
                label="Action"
                columnKey="action"
                sortConfig={sortConfig}
                onSortChange={onSortChange}
              />
            </TableCell>
            <TableCell
              sx={{
                width: 180,
                height: WATCHLIST_TABLE_ROW_HEIGHT,
                py: 0.75,
                color: tokens.colors.fontColour,
                backgroundColor: tokens.colors.defaultGridHeaderBackground,
                borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
              }}
            >
              <SortableHeaderLabel
                label="Identifier"
                columnKey="cusip"
                sortConfig={sortConfig}
                onSortChange={onSortChange}
              />
            </TableCell>
            <TableCell
              sx={{
                width: 140,
                height: WATCHLIST_TABLE_ROW_HEIGHT,
                py: 0.75,
                color: tokens.colors.fontColour,
                backgroundColor: tokens.colors.defaultGridHeaderBackground,
                borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
              }}
            >
              <SortableHeaderLabel
                label="Type"
                columnKey="bondType"
                sortConfig={sortConfig}
                onSortChange={onSortChange}
              />
            </TableCell>
            <TableCell
              sx={{
                height: WATCHLIST_TABLE_ROW_HEIGHT,
                py: 0.75,
                color: tokens.colors.fontColour,
                backgroundColor: tokens.colors.defaultGridHeaderBackground,
                borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
              }}
            >
              <SortableHeaderLabel
                label="Description"
                columnKey={descriptionSortKey}
                sortConfig={sortConfig}
                onSortChange={onSortChange}
              />
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {bonds.map((bond) => {
            const disabled = isActionDisabled?.(bond) ?? false;
            const checkboxChecked = isCheckboxChecked?.(bond) ?? false;
            const checkboxDisabled = isCheckboxDisabled?.(bond) ?? false;
            const discarded = isDiscarded?.(bond) ?? false;
            const resolvedActionLabel = getActionLabel?.(bond) ?? actionLabel;
            const isUndoAction = resolvedActionLabel === "Undo";
            const isDeleteAction = resolvedActionLabel === "Delete";
            const actionColor = isUndoAction
              ? tokens.palette.primary
              : discarded
              ? tokens.colors.fontColour85
              : isDeleteAction
              ? tokens.palette.danger
              : tokens.palette.primary;
            const checkboxStrokeColor = checkboxDisabled
              ? tokens.colors.fontColour50
              : tokens.colors.darkgray;
            const checkedCheckboxColor = checkboxDisabled
              ? tokens.colors.fontColour50
              : tokens.palette.primary;
            const checkboxFillColor = checkboxDisabled
              ? withAlpha(tokens.colors.fontColour50, 0.16)
              : "transparent";
            const isActionCellDisabled = useCheckboxAction ? checkboxDisabled : disabled;
            const resolvedDescription = `${bond?.securityDesc ?? bond?.description ?? ""}`.trim();

            const handleActionCellClick = () => {
              if (useCheckboxAction) {
                if (checkboxDisabled) {
                  return;
                }

                onCheckboxToggle?.(bond, !checkboxChecked);
                return;
              }

              if (disabled) {
                return;
              }

              onActionClick?.(bond);
            };

            return (
              <TableRow
                key={bond.cusip}
                hover
                sx={{
                  height: WATCHLIST_TABLE_ROW_HEIGHT,
                  opacity: discarded ? 0.55 : 1,
                  backgroundColor: discarded
                    ? withAlpha(tokens.colors.fontColour85, 0.08)
                    : "transparent",
                  "& .MuiTableCell-root": {
                    height: WATCHLIST_TABLE_ROW_HEIGHT,
                    py: 0.5,
                    borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    backgroundColor: discarded
                      ? withAlpha(tokens.colors.fontColour85, 0.08)
                      : tokens.colors.bodyBackgroundColor,
                  },
                }}
              >
                <TableCell
                  onClick={handleActionCellClick}
                  sx={{
                    width: 120,
                    py: 0.5,
                    textAlign: "center",
                    cursor: isActionCellDisabled ? "default" : "pointer",
                  }}
                >
                  {useCheckboxAction ? (
                    <Checkbox
                      size="small"
                      checked={checkboxChecked}
                      disabled={checkboxDisabled}
                      icon={
                        <WatchlistCheckboxIcon
                          strokeColor={checkboxStrokeColor}
                          fillColor={checkboxFillColor}
                        />
                      }
                      checkedIcon={
                        <WatchlistCheckboxIcon
                          strokeColor={checkedCheckboxColor}
                          fillColor={checkboxFillColor}
                          checkColor={checkedCheckboxColor}
                        />
                      }
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        onCheckboxToggle?.(bond, event.target.checked);
                      }}
                      sx={{
                        p: 0,
                        "&.Mui-disabled": {
                          opacity: 1,
                        },
                      }}
                    />
                  ) : useIconAction ? (
                    <IconButton
                      size="small"
                      disabled={disabled}
                      aria-label={resolvedActionLabel}
                      title={resolvedActionLabel}
                      onClick={(event) => {
                        event.stopPropagation();
                        onActionClick?.(bond);
                      }}
                      sx={{
                        color: actionColor,
                        p: 0,
                        m: 0,
                        minWidth: 0,
                        minHeight: 0,
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: discarded
                            ? withAlpha(tokens.colors.fontColour85, 0.08)
                            : withAlpha(actionColor, 0.08),
                        },
                      }}
                    >
                      {useIconAction && resolvedActionLabel === "Undo" ? (
                        <UndoOutlined sx={{ fontSize: 21 }} />
                      ) : (
                        <DeleteOutline sx={{ fontSize: 21 }} />
                      )}
                    </IconButton>
                  ) : (
                    <Button
                      variant="outlined"
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        onActionClick?.(bond);
                      }}
                      sx={{
                        minWidth: "72px",
                        height: 26,
                        px: 1.5,
                        py: 0.25,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 700,
                        boxShadow: "none",
                        color: actionColor,
                        borderColor: actionColor,
                        "&:hover": {
                          borderColor: actionColor,
                          backgroundColor: discarded
                            ? withAlpha(tokens.colors.fontColour85, 0.08)
                            : withAlpha(actionColor, 0.08),
                        },
                      }}
                    >
                      {resolvedActionLabel}
                    </Button>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    width: 180,
                    py: 0.5,
                    color: tokens.colors.fontColour,
                    fontWeight: 600,
                  }}
                >
                  {bond.cusip || "---"}
                </TableCell>
                <TableCell
                  sx={{
                    width: 140,
                    py: 0.5,
                    color: tokens.colors.fontColour85,
                    fontWeight: 500,
                  }}
                >
                  {`${bond?.bondType ?? ""}`.trim() ? bond.bondType : "---"}
                </TableCell>
                <TableCell
                  sx={{
                    color: tokens.colors.fontColour85,
                    width: "auto",
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: tokens.colors.fontColour85,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={resolvedDescription || "---"}
                  >
                    {resolvedDescription || "---"}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MAWatchlistTableSection;
